// Custom media gallery extension for FAC theme
// This file extends the default media-gallery functionality without modifying the original file

if (!customElements.get('fac-media-gallery')) {
  customElements.define(
    'fac-media-gallery',
    class FacMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          counter: {
            current: this.querySelector('.slider-counter--current'),
            total: this.querySelector('.slider-counter--total'),
          },
          mediaLabels: this.querySelector('.fac-gallery-media-labels'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileBreakpoint = window.matchMedia('(max-width: 767px)');
        this.isModalOpen = false;

        // 実際にレンダリングされたスライド数を取得
        // Liquidの計算とDOM上の実際の数が一致しない場合があるため、DOMから直接カウント
        const actualSlides = this.elements.viewer.querySelectorAll('[id^="Slide-"]');
        this.totalMediaCount = actualSlides.length;

        // 初期表示時にカウンターを更新
        if (this.elements.counter.total && this.totalMediaCount > 0) {
          this.elements.counter.total.textContent = this.totalMediaCount;
        }

        // SliderComponentが総数を上書きしないように、pageTotalElementへの参照を削除
        if (this.elements.viewer && this.elements.viewer.pageTotalElement) {
          this.elements.viewer.pageTotalElement = null;
        }

        // 初期状態の矢印表示を更新
        this.updateNavArrowsVisibility();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // バリエーション変更を監視
        this.setupVariantChangeListener();

        // スマホでのモーダル機能をセットアップ
        this.setupMobileModal();

        // 初期ロード時にバリエーションパラメータを確認してactive状態を調整
        this.handleInitialVariantState();

        // 初期ロード時にis-active要素にスクロール
        this.scrollToActiveSlide();

        // 初期表示時のラベル表示を設定（activeスライドのmedia-idを取得）
        const activeSlide = this.elements.viewer?.querySelector('.is-active');
        if (activeSlide && activeSlide.dataset.mediaId) {
          this.updateMediaLabelVisibility(activeSlide.dataset.mediaId);
        }
      }

      handleInitialVariantState() {
        // URLに?variant=パラメータがあるかチェック
        const urlParams = new URLSearchParams(window.location.search);
        const hasVariantParam = urlParams.has('variant');

        if (!hasVariantParam) {
          // variant パラメータがない場合、常に1番目（position 0）をactiveにする
          const firstSlide = this.elements.viewer?.querySelector('[data-media-id]');
          const firstThumbnail = this.querySelector('slider-component[id*="GalleryThumbnails"] button.thumbnail');

          // スライド要素のis-activeとサムネイルのaria-currentを削除（バリエーションラベルは触らない）
          this.elements.viewer?.querySelectorAll('[data-media-id].is-active').forEach(slide => {
            slide.classList.remove('is-active');
          });
          this.querySelectorAll('button.thumbnail[aria-current="true"]').forEach(thumb => {
            thumb.removeAttribute('aria-current');
          });

          // 1番目をactiveにする
          if (firstSlide) {
            firstSlide.classList.add('is-active');
          }
          if (firstThumbnail) {
            firstThumbnail.setAttribute('aria-current', 'true');
          }
        } else {
          // variant パラメータがある場合、バリアント画像（data-variant-media属性を持つ要素）をactiveにする
          const variantSlide = this.elements.viewer?.querySelector('[data-variant-media="true"]');
          const variantThumbnail = this.querySelector('slider-component[id*="GalleryThumbnails"] button[data-variant-thumbnail="true"]');

          if (variantSlide && variantThumbnail) {
            // スライド要素のis-activeとサムネイルのaria-currentを削除（バリエーションラベルは触らない）
            this.elements.viewer?.querySelectorAll('[data-media-id].is-active').forEach(slide => {
              slide.classList.remove('is-active');
            });
            this.querySelectorAll('button.thumbnail[aria-current="true"]').forEach(thumb => {
              thumb.removeAttribute('aria-current');
            });

            // バリアント画像をactiveにする
            variantSlide.classList.add('is-active');
            variantThumbnail.setAttribute('aria-current', 'true');
          }
        }
        // バリエーションラベルのis-activeはLiquid側で制御（JavaScriptでは触らない）
      }

      scrollToActiveSlide() {
        // is-active クラスを持つスライドを探す
        const activeSlide = this.elements.viewer?.querySelector('.is-active');
        if (!activeSlide) return;

        // アクティブなスライドの位置にスクロール
        const viewerList = this.elements.viewer.querySelector('ul');
        if (viewerList) {
          viewerList.scrollTo({
            left: activeSlide.offsetLeft,
            behavior: 'instant', // ページロード時は即座にスクロール
          });
        }
      }

      snapToNearestSlide() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;

        const slider = this.elements.viewer.slider;
        const slides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));

        if (slides.length === 0) return;

        // 現在のスクロール位置から最も近いスライドを見つける
        let nearestSlide = slides[0];
        let minDistance = Math.abs(slider.scrollLeft - slides[0].offsetLeft);

        slides.forEach((slide) => {
          const distance = Math.abs(slider.scrollLeft - slide.offsetLeft);
          if (distance < minDistance) {
            minDistance = distance;
            nearestSlide = slide;
          }
        });

        // 最も近いスライドにスクロール
        // 既に正しい位置にいる場合（許容誤差5px以内）はスクロールしない
        if (minDistance > 5) {
          slider.scrollTo({
            left: nearestSlide.offsetLeft,
            behavior: 'auto', // モバイル互換性のためautoに変更
          });
        }
      }

      setupVariantChangeListener() {
        // URL変更を監視してバリエーション変更を検出
        if (this.elements.variantLabel) {
          const observer = new MutationObserver(() => {
            this.updateVariantLabel();
          });

          // product-info要素の属性変更を監視
          const productInfo = document.querySelector('product-info');
          if (productInfo) {
            observer.observe(productInfo, {
              attributes: true,
              attributeFilter: ['data-url'],
            });
          }

          // バリアントピッカーの変更イベントを監視
          document.addEventListener('variant:change', (event) => {
            this.updateVariantLabel(event.detail);
          });
        }
      }

      updateVariantLabel() {
        if (!this.elements.variantLabel) return;

        // URLパラメータからバリアント情報を取得
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');

        if (variantId) {
          // バリアント情報を取得して表示を更新
          // ここでは簡易的に、アクティブなスライドの情報から判断
          const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
          if (activeSlide) {
            this.elements.variantLabel.style.opacity = '1';
          }
        }
      }

      updateNavArrowsVisibility() {
        if (!this.elements.viewer || !this.elements.viewer.prevButton || !this.elements.viewer.nextButton) return;

        const slider = this.elements.viewer.slider;
        if (!slider) return;

        const slides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
        if (slides.length === 0) return;

        // アクティブなスライドのインデックスを取得
        const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (!activeSlide) return;

        const currentIndex = slides.indexOf(activeSlide);
        const isFirst = currentIndex === 0;
        const isLast = currentIndex === slides.length - 1;

        // 前ボタンの表示/非表示
        if (isFirst) {
          this.elements.viewer.prevButton.classList.add('is-hidden');
          this.elements.viewer.prevButton.setAttribute('disabled', '');
        } else {
          this.elements.viewer.prevButton.classList.remove('is-hidden');
          this.elements.viewer.prevButton.removeAttribute('disabled');
        }

        // 次ボタンの表示/非表示
        if (isLast) {
          this.elements.viewer.nextButton.classList.add('is-hidden');
          this.elements.viewer.nextButton.setAttribute('disabled', '');
        } else {
          this.elements.viewer.nextButton.classList.remove('is-hidden');
          this.elements.viewer.nextButton.removeAttribute('disabled');
        }
      }

      updateCounter(currentIndex) {
        // 現在のインデックスのみ更新（総数は初期化時に設定済み）
        if (this.elements.counter.current) {
          this.elements.counter.current.textContent = currentIndex;
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        // スライド自体のis-activeクラスを更新
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        event.detail.currentElement.classList.add('is-active');

        // カウンターの現在位置のみ更新（総数は変更しない）
        this.updateCounter(event.detail.currentPage);

        // 矢印の表示/非表示を更新
        this.updateNavArrowsVisibility();

        // メディアラベルの表示/非表示を更新（media-idベース）
        this.updateMediaLabelVisibility(event.detail.currentElement.dataset.mediaId);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Slide to the active media item with smooth scroll behavior
          if (activeMedia.parentElement) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth',
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);

        // メディアラベルの表示/非表示を更新（media-idベース）
        this.updateMediaLabelVisibility(mediaId);

        // ナビゲーション矢印の表示/非表示を更新
        // setTimeoutでスクロール完了後に実行
        window.setTimeout(() => {
          this.updateNavArrowsVisibility();
        }, 100);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      setupMobileModal() {
        // スマホ幅でのみモーダル機能を有効化
        if (!this.mobileBreakpoint.matches) return;

        // カウンター要素をクリック可能にする
        const counterElement = this.querySelector('.fac-gallery-counter');
        if (counterElement) {
          counterElement.style.pointerEvents = 'auto';
          counterElement.style.cursor = 'pointer';
          counterElement.addEventListener('click', this.openModal.bind(this));
        }

        // サムネイルのモーダルイベントを設定
        this.setupMobileModalThumbnails();
      }

      setupMobileModalThumbnails() {
        // スマホ幅でのみモーダル機能を有効化
        if (!this.mobileBreakpoint.matches) return;

        // サムネイルにクリックイベントを追加（モーダルを閉じてスライド）
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
            const button = thumbnail.querySelector('button');
            const mediaId = thumbnail.dataset.target;

            // 新しいクリックハンドラーを作成
            const newClickHandler = (e) => {
              if (this.isModalOpen) {
                e.preventDefault();
                e.stopPropagation();
                this.closeModal();
                // モーダルを閉じた後にスライド
                setTimeout(() => {
                  this.setActiveMedia(mediaId, false);
                }, 300);
              }
            };

            // イベントリスナーを先頭に追加（キャプチャフェーズで実行）
            button.addEventListener('click', newClickHandler, true);
          });
        }
      }

      openModal() {
        if (!this.mobileBreakpoint.matches) return;

        this.isModalOpen = true;
        const thumbnails = this.elements.thumbnails;
        if (!thumbnails) return;

        // オーバーレイを作成
        if (!this.overlay) {
          this.overlay = document.createElement('div');
          this.overlay.className = 'fac-gallery-modal-overlay';
          this.overlay.addEventListener('click', this.closeModal.bind(this));
          document.body.appendChild(this.overlay);
        }

        // オーバーレイを表示
        requestAnimationFrame(() => {
          this.overlay.classList.add('fac-gallery-modal-overlay-active');
        });

        // モーダル用のクラスを追加
        thumbnails.classList.add('fac-thumbnail-modal-active');

        // ヘッダー領域を作成（クローズボタン + タイトル + スペーサー）
        if (!this.modalHeader) {
          this.modalHeader = document.createElement('div');
          this.modalHeader.className = 'fac-gallery-modal-close';
          this.modalHeader.innerHTML = `
            <button class="fac-gallery-modal-close-button" aria-label="Close gallery">
              <span class="fac-gallery-modal-close-icon">
                <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path>
                </svg>
              </span>
            </button>
            <div class="fac-gallery-modal-title">商品画像一覧</div>
            <div class="fac-gallery-modal-spacer"></div>
          `;

          // クローズボタンにイベントリスナーを追加
          const closeButton = this.modalHeader.querySelector('.fac-gallery-modal-close-button');
          closeButton.addEventListener('click', this.closeModal.bind(this));

          thumbnails.appendChild(this.modalHeader);
        }

        // bodyのスクロールを無効化
        document.body.style.overflow = 'hidden';
      }

      closeModal() {
        if (!this.isModalOpen) return;

        this.isModalOpen = false;
        const thumbnails = this.elements.thumbnails;
        if (!thumbnails) return;

        // オーバーレイを非表示
        if (this.overlay) {
          this.overlay.classList.remove('fac-gallery-modal-overlay-active');
        }

        // モーダル用のクラスを削除
        thumbnails.classList.remove('fac-thumbnail-modal-active');

        // bodyのスクロールを有効化
        document.body.style.overflow = '';
      }

      updateMediaLabelVisibility(currentMediaId) {
        if (!this.elements.mediaLabels) return;

        const labels = this.elements.mediaLabels.querySelectorAll('.fac-gallery-media-label');

        // 全てのラベルを非表示
        labels.forEach((label) => label.classList.remove('is-visible'));

        // 現在のメディアに対応するラベルを表示
        const currentLabel = this.elements.mediaLabels.querySelector(
          `.fac-gallery-media-label[data-media-id="${currentMediaId}"]`
        );

        if (currentLabel) {
          currentLabel.classList.add('is-visible');
        }
      }
    }
  );
}
